import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  Send,
  Eye,
  X,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const DOCUMENT_TYPES = [
  { id: "driving_license", name: "Driving License", has_expiry: true },
  { id: "rc_book", name: "RC Book", has_expiry: false },
  { id: "insurance", name: "Vehicle Insurance", has_expiry: true },
  { id: "aadhar", name: "Aadhar Card", has_expiry: false },
  { id: "pan", name: "PAN Card", has_expiry: false },
  { id: "vehicle_photo", name: "Vehicle Photo", has_expiry: false },
  { id: "profile_photo", name: "Profile Photo", has_expiry: false },
];

export const DocumentsManagement = () => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, no_docs, pending, verified
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDocs, setDriverDocs] = useState(null);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestDocs, setRequestDocs] = useState([]);
  const [requestMessage, setRequestMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchDriverDocumentStatus();
    }
  }, [user?.id]);

  const fetchDriverDocumentStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/drivers/document-status?admin_id=${user.id}`);
      setDrivers(res.data.drivers || []);
    } catch (err) {
      toast.error("Failed to load document status");
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDocuments = async (driverId) => {
    try {
      const res = await axios.get(`${API}/driver/${driverId}/documents`);
      setDriverDocs(res.data);
    } catch (err) {
      toast.error("Failed to load driver documents");
    }
  };

  const handleViewDriver = async (driver) => {
    setSelectedDriver(driver);
    await fetchDriverDocuments(driver.driver_id);
    setShowDocDialog(true);
  };

  const handleVerifyDocument = async (docType, status, reason = null) => {
    try {
      await axios.put(
        `${API}/admin/driver/${selectedDriver.driver_id}/documents/${docType}/verify`,
        { status, rejection_reason: reason }
      );
      toast.success(`Document ${status}`);
      await fetchDriverDocuments(selectedDriver.driver_id);
      fetchDriverDocumentStatus();
    } catch (err) {
      toast.error("Failed to update document status");
    }
  };

  const handleRequestDocuments = async () => {
    if (requestDocs.length === 0) {
      toast.error("Please select at least one document type");
      return;
    }
    try {
      await axios.post(`${API}/admin/driver/${selectedDriver.driver_id}/request-documents`, {
        document_types: requestDocs,
        message: requestMessage || "Please upload the requested documents"
      });
      toast.success("Document request sent to driver");
      setShowRequestDialog(false);
      setRequestDocs([]);
      setRequestMessage("");
    } catch (err) {
      toast.error("Failed to send document request");
    }
  };

  const filteredDrivers = drivers.filter((driver) => {
    const matchesSearch = !searchTerm ||
      driver.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm);
    
    let matchesFilter = true;
    switch (filter) {
      case "no_docs":
        matchesFilter = driver.total_documents === 0;
        break;
      case "pending":
        matchesFilter = driver.pending > 0;
        break;
      case "verified":
        matchesFilter = driver.all_verified;
        break;
      default:
        matchesFilter = true;
    }
    
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case "verified":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "pending":
        return <Clock className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="documents-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Driver Documents</h2>
          <p className="text-muted-foreground">
            {drivers.length} drivers | {drivers.filter(d => d.all_verified).length} fully verified
          </p>
        </div>
        <Button variant="outline" onClick={fetchDriverDocumentStatus}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="p-3 md:p-4 bg-card rounded-xl border border-border">
          <p className="text-2xl md:text-3xl font-bold">{drivers.length}</p>
          <p className="text-xs md:text-sm text-muted-foreground">Total Drivers</p>
        </div>
        <div className="p-3 md:p-4 bg-green-50 rounded-xl border border-green-200">
          <p className="text-2xl md:text-3xl font-bold text-green-600">
            {drivers.filter(d => d.all_verified).length}
          </p>
          <p className="text-xs md:text-sm text-green-600">Fully Verified</p>
        </div>
        <div className="p-3 md:p-4 bg-amber-50 rounded-xl border border-amber-200">
          <p className="text-2xl md:text-3xl font-bold text-amber-600">
            {drivers.filter(d => d.pending > 0).length}
          </p>
          <p className="text-xs md:text-sm text-amber-600">Pending Review</p>
        </div>
        <div className="p-3 md:p-4 bg-red-50 rounded-xl border border-red-200">
          <p className="text-2xl md:text-3xl font-bold text-red-600">
            {drivers.filter(d => d.total_documents === 0).length}
          </p>
          <p className="text-xs md:text-sm text-red-600">No Documents</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background"
        >
          <option value="all">All Drivers</option>
          <option value="no_docs">No Documents</option>
          <option value="pending">Pending Review</option>
          <option value="verified">Fully Verified</option>
        </select>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredDrivers.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-6 text-center text-muted-foreground">
            No drivers found
          </div>
        ) : (
          filteredDrivers.map((driver) => (
            <div key={driver.driver_id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium">{driver.driver_name}</p>
                  <p className="text-xs text-muted-foreground">{driver.phone}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  driver.all_verified ? 'bg-green-100 text-green-700' :
                  driver.pending > 0 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {driver.all_verified ? 'Verified' : driver.pending > 0 ? 'Pending' : 'Incomplete'}
                </span>
              </div>
              
              <div className="grid grid-cols-4 gap-2 text-center mb-3">
                <div>
                  <p className="text-lg font-bold">{driver.total_documents}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-green-600">{driver.verified}</p>
                  <p className="text-xs text-muted-foreground">Verified</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-amber-600">{driver.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-red-600">{driver.rejected}</p>
                  <p className="text-xs text-muted-foreground">Rejected</p>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${driver.all_verified ? 'bg-green-500' : driver.pending > 0 ? 'bg-amber-500' : 'bg-gray-400'}`}
                    style={{ width: `${driver.completion_percentage}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{driver.completion_percentage}% complete</p>
              </div>
              
              <Button className="w-full" variant="outline" onClick={() => handleViewDriver(driver)}>
                View Documents <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead>Pending</TableHead>
              <TableHead>Rejected</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDrivers.map((driver) => (
              <TableRow key={driver.driver_id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{driver.driver_name}</p>
                    <p className="text-xs text-muted-foreground">{driver.phone}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-mono">{driver.total_documents}/{DOCUMENT_TYPES.length}</span>
                </TableCell>
                <TableCell>
                  <span className="text-green-600 font-medium">{driver.verified}</span>
                </TableCell>
                <TableCell>
                  <span className="text-amber-600 font-medium">{driver.pending}</span>
                </TableCell>
                <TableCell>
                  <span className="text-red-600 font-medium">{driver.rejected}</span>
                </TableCell>
                <TableCell>
                  <div className="w-24">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${driver.all_verified ? 'bg-green-500' : driver.pending > 0 ? 'bg-amber-500' : 'bg-gray-400'}`}
                        style={{ width: `${driver.completion_percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{driver.completion_percentage}%</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => handleViewDriver(driver)}>
                    View <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Driver Documents Dialog */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-base sm:text-lg">Documents - {selectedDriver?.driver_name}</span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setShowRequestDialog(true);
                }}
                className="w-full sm:w-auto"
              >
                <Send className="w-4 h-4 mr-2" /> Request Documents
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {driverDocs && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {driverDocs.documents.map((doc) => (
                  <div 
                    key={doc.type}
                    className={`p-4 rounded-lg border ${
                      doc.status === "verified" ? "border-green-200 bg-green-50" :
                      doc.status === "rejected" ? "border-red-200 bg-red-50" :
                      doc.status === "pending" ? "border-amber-200 bg-amber-50" :
                      "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium text-sm md:text-base">{doc.name}</span>
                      </div>
                      {getStatusIcon(doc.status)}
                    </div>

                    {doc.uploaded ? (
                      <>
                        <p className="text-xs text-muted-foreground mb-2">
                          Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                        {doc.document_number && (
                          <p className="text-xs mb-2">Number: {doc.document_number}</p>
                        )}
                        {doc.expiry_date && (
                          <p className="text-xs mb-2">Expires: {doc.expiry_date}</p>
                        )}
                        {doc.rejection_reason && (
                          <p className="text-xs text-red-600 mb-2">Reason: {doc.rejection_reason}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {doc.url && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setPreviewUrl(doc.url)}
                              className="text-xs px-2 py-1 h-8"
                            >
                              <Eye className="w-3 h-3 mr-1" /> View
                            </Button>
                          )}
                          {doc.status === "pending" && (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-green-500 hover:bg-green-600 text-xs px-3 py-1 h-8"
                                onClick={() => handleVerifyDocument(doc.type, "verified")}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Verify
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                className="text-xs px-3 py-1 h-8"
                                onClick={() => {
                                  const reason = prompt("Rejection reason:");
                                  if (reason) handleVerifyDocument(doc.type, "rejected", reason);
                                }}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {doc.status === "rejected" && (
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600 text-xs px-3 py-1 h-8"
                              onClick={() => handleVerifyDocument(doc.type, "verified")}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Re-verify
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Not uploaded</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Documents Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Documents from {selectedDriver?.driver_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Select documents to request:</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {DOCUMENT_TYPES.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted/50">
                    <input
                      type="checkbox"
                      checked={requestDocs.includes(doc.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRequestDocs([...requestDocs, doc.id]);
                        } else {
                          setRequestDocs(requestDocs.filter(d => d !== doc.id));
                        }
                      }}
                    />
                    <span className="text-sm">{doc.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Message (optional):</Label>
              <Input 
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Please upload the requested documents"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestDocuments}>
              <Send className="w-4 h-4 mr-2" /> Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Document Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {previewUrl && (
              <img src={previewUrl} alt="Document" className="max-w-full max-h-[60vh] object-contain" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsManagement;
